import { useMemo } from 'react'

import { useListInherentRiskCategories } from '@/api/company.hook'
import {
  type InherentRiskGroup,
  InherentRiskQuestionType,
} from '@/gen/inventory/v1/company_service_pb'

import { calculatePercentage } from '@/lib/collection/math-utils'
import type { IrqAnswerStore } from '@/lib/irq-answer-store/irq-answer-store.hook'

import { IrqFreeTextInput } from '@/pages/assessment/irq/irq-free-text-input'
import { SET_IRQ_MODAL_LABEL } from '@/pages/company-drawer/company-overview/set-irq-modal/labels'
import { LemaSuggestionsCard } from '@/pages/company-drawer/company-overview/set-irq-modal/lema-suggestions-card/lema-suggestions-card'

import { ProgressBar } from '@/components/lemmala-design-system/progress-bar/progress-bar'
import { RadioGroup } from '@/components/lemmala-design-system/radio-group/radio-group'
import { Select } from '@/components/lemmala-design-system/select/select'
import { IRQAnswerTag } from '@/components/shared/irq-answer-tag/irq-answer-tag'
import { RiskBadge } from '@/components/shared/risk-badge/risk-badge'

type IrqQuestionCardProps = {
  group: InherentRiskGroup
  lemaGroup?: InherentRiskGroup
  localState: IrqAnswerStore
  currentIndex: number // 1-based
  totalCount: number
  businessOwnerName?: string
}

export const IrqQuestionCard = ({
  group,
  lemaGroup,
  localState,
  currentIndex,
  totalCount,
  businessOwnerName,
}: IrqQuestionCardProps) => {
  const { data: inherentRiskCategories } = useListInherentRiskCategories()

  const options = useMemo(() => {
    const cat = inherentRiskCategories.find((c) => c.id === group.id)
    return (cat?.items ?? []).filter((item) => item.enabled)
  }, [inherentRiskCategories, group.id])

  const selectOptions = useMemo(
    () =>
      options.map((option) => ({
        value: option.id ?? '',
        displayName: option.displayName,
        label: (
          <span className='flex items-center gap-2'>
            <RiskBadge
              riskLevel={option.severity}
              size='icon'
              className='!bg-transparent !p-0 !text-current'
            />
            <span>{option.displayName}</span>
          </span>
        ),
      })),
    [options],
  )

  const selectedIds = useMemo(
    () => localState.answers[group.id]?.selectedOptionIds ?? [],
    [localState.answers, group.id],
  )

  const { question, description, id, type } = group

  const handleToggle = (optionId: string) =>
    localState.updateQuestion(id, { toggleOption: optionId })

  const lemaSuggestions = useMemo(
    () => lemaGroup?.inherentRiskItems ?? [],
    [lemaGroup?.inherentRiskItems],
  )

  const suggestedOptionIds = useMemo(
    () => new Set(lemaSuggestions.map((item) => item.id).filter((id): id is string => !!id)),
    [lemaSuggestions],
  )

  const applyAllSuggestions = () => {
    suggestedOptionIds.forEach((optionId) => {
      if (!selectedIds.includes(optionId)) {
        localState.updateQuestion(id, { toggleOption: optionId })
      }
    })
  }

  const removeAllSuggestions = () => {
    suggestedOptionIds.forEach((optionId) => {
      if (selectedIds.includes(optionId)) {
        localState.updateQuestion(id, { toggleOption: optionId })
      }
    })
  }

  const progressPct = calculatePercentage({ amount: currentIndex, total: totalCount })

  return (
    <div className='flex h-full flex-col overflow-hidden rounded-md border border-main-border-default'>
      <ProgressBar value={progressPct} />
      <div className='min-h-0 flex-1 overflow-y-auto p-6'>
        <div className='mb-3 flex items-baseline gap-2'>
          <span className='text-main-foreground-tertiary text-body-default'>
            {currentIndex}/{totalCount}.
          </span>
          <h3 className='text-main-foreground-primary text-label-large'>{question}</h3>
        </div>
        {description && (
          <p className='mb-3 text-main-foreground-secondary text-body-default'>{description}</p>
        )}

        {type === InherentRiskQuestionType.FREE_TEXT && (
          <IrqFreeTextInput
            value={localState.answers[id]?.content ?? ''}
            onChange={(content) =>
              localState.updateQuestion(id, { content, selectedOptionIds: [] })
            }
          />
        )}

        {type === InherentRiskQuestionType.SINGLE_SELECT && (
          <div className='flex flex-col gap-4'>
            <RadioGroup
              value={selectedIds[0] ?? ''}
              onValueChange={(value) =>
                localState.updateQuestion(id, { selectedOptionIds: [value] })
              }
              options={selectOptions}
              direction='vertical'
            />
            <LemaSuggestionsCard
              suggestions={lemaSuggestions}
              selectedIds={selectedIds}
              categoryEnum={group.categoryEnum}
              mode='single'
              businessOwnerName={businessOwnerName}
            />
          </div>
        )}

        {type !== InherentRiskQuestionType.FREE_TEXT &&
          type !== InherentRiskQuestionType.SINGLE_SELECT && (
            <div className='flex flex-col gap-4'>
              <Select<string[]>
                mode='multiple'
                value={selectedIds}
                onChange={(values) => localState.updateQuestion(id, { selectedOptionIds: values })}
                placeholder={SET_IRQ_MODAL_LABEL.SELECT_OPTIONS}
                options={selectOptions}
                optionFilterProp='displayName'
                className='w-full'
                tagRender={({ value, closable, onClose }) => {
                  const id = value as string
                  // Saved answers can reference options that have since been disabled server-side
                  // (options is filtered to enabled). Skip rendering those orphans.
                  const opt = options.find((o) => o.id === id)
                  if (!opt) return <></>
                  return (
                    <IRQAnswerTag
                      label={opt.displayName}
                      severity={opt.severity}
                      fromLemaSuggestions={suggestedOptionIds.has(id)}
                      onRemove={closable ? onClose : undefined}
                    />
                  )
                }}
              />
              <LemaSuggestionsCard
                suggestions={lemaSuggestions}
                selectedIds={selectedIds}
                categoryEnum={group.categoryEnum}
                onToggle={handleToggle}
                onApplyAll={applyAllSuggestions}
                onRemoveAll={removeAllSuggestions}
                businessOwnerName={businessOwnerName}
              />
            </div>
          )}
      </div>
    </div>
  )
}
